// D98.2 / D96.8 - labels and formatting for the Profitability tab and the invoice header. The figures (Sell net of credit
// notes, cost, margin) are NOT worked out here: `invoiceProfit` in utils/clientInvoiceProfit is the one owner (D119.14),
// shared with the Margin-by-vessel report. The page feeds its rows to `orderProgress` (D183.1), whose actual figures the
// header strip and the tab show (D183.3/4); this file only labels the rows and formats money.
// A missing selling total or cost is BLANK (null), never 0; a cost of 0 is "no cost data".
//
// D146 (item 14) made "selling" net of credit notes; cost is untouched (a credit note does not hand
// back what USC already paid its supplier). The stored invoice summary (utils/clientInvoiceSummary,
// shared with Accounts and the PDF) stays gross on purpose (D146.3).
import { num } from '../../utils/invoiceLineMath';
import { ttdAmount } from '../../utils/clientInvoiceTtd';
import { invoiceProfit, type ProfitCreditNote, type ProfitRow } from '../../utils/clientInvoiceProfit';
import { formatCurrency } from '../../utils/priceUtils';

export interface ProfitabilityRow extends ProfitRow {
  label: string;
}

/**
 * One credit note in the shape `lib/creditNoteApi.CreditNote` already uses client-side (item field
 * `extensionPrice`, linked invoices as ids + `linkedInvoiceTotals`), not the server document's own.
 */
export interface CreditNoteForProfitability {
  status?: string | null;
  linkedInvoices?: ReadonlyArray<string> | null;
  linkedInvoiceTotals?: ReadonlyArray<number | null> | null;
  items?: ReadonlyArray<{ sourceInvoiceLineNumber?: number | null; extensionPrice?: unknown }> | null;
}

/** Field names only: the client note read in the owner's (server document) shape. No credit logic here. */
function asProfitNote(cn: CreditNoteForProfitability): ProfitCreditNote {
  return {
    status: cn?.status,
    linkedInvoices: (cn?.linkedInvoices || []).map((clientInvoiceId, i) => ({ clientInvoiceId, invoiceTotal: cn?.linkedInvoiceTotals?.[i] })),
    items: (cn?.items || []).map((it) => ({ sourceInvoiceLineNumber: it?.sourceInvoiceLineNumber, extension: it?.extensionPrice })),
  };
}

/**
 * The invoice's labelled rows from the one owner (`invoiceProfit`, net of credit notes): what `orderProgress` starts from.
 * Its totals are not read anywhere (the header and the tab show `orderProgress`'s actual ones), so they are not returned.
 */
export function buildProfitability(
  invoice: { items?: ReadonlyArray<any> | null } | null | undefined,
  creditNotes: ReadonlyArray<CreditNoteForProfitability> | null | undefined,
  invoiceId: unknown
): { rows: ProfitabilityRow[] } {
  const list = Array.isArray(invoice?.items) ? invoice!.items! : [];
  const { rows } = invoiceProfit({ items: list }, (creditNotes || []).map(asProfitNote), invoiceId);
  return {
    rows: rows.map((row, index) => ({
      ...row,
      label: list[index]?.itemName || list[index]?.itemDescription || `Line ${index + 1}`,
    })),
  };
}

export interface TtdFigures {
  selling: number | null;
  cost: number | null;
  /** The footer's Mark-up: selling - cost over the lines that have a cost. */
  markup: number | null;
}

/**
 * D119.13: the footer's money figures in TT$ at the rate PINNED on the invoice (`ttdAmount` is the one
 * owner, twin of the Margin-by-vessel report's). Each is the SAME figure the footer shows (`orderProgress`'s actual
 * totals since D183.4, net of credits since D146), converted on its own, so the two strips never disagree: no TT$ figure is
 * worked out from other TT$ figures. Nothing here reads a rate. Missing is null, never 0: a cost of 0 is
 * "no cost data" (as everywhere above), so it has no TT$ cost.
 */
export function buildTtdFigures(
  totals: { selling?: number | null; cost?: number | null; margin?: number | null },
  currency: string | null | undefined,
  usdToTtd: number | null | undefined,
): TtdFigures {
  const rawCost = num(totals.cost);
  return {
    selling: ttdAmount(totals.selling, currency, usdToTtd),
    cost: rawCost !== null && rawCost > 0 ? ttdAmount(rawCost, currency, usdToTtd) : null,
    markup: ttdAmount(totals.margin, currency, usdToTtd),
  };
}

/** Money for a cell: blank (null / non-number) shows "--", never 0.00. */
export function moneyOrBlank(value: number | null | undefined, currency?: string | null): string {
  return typeof value === 'number' && Number.isFinite(value) ? formatCurrency(value, currency || null, { ceil: false }) : '--';
}

export const pctOrBlank = (value: number | null | undefined): string =>
  typeof value === 'number' && Number.isFinite(value) ? `${value}%` : '--';
